"use strict";
Object.defineProperty(exports, "__esModule", {
    value: true
});
Object.defineProperty(exports, "default", {
    enumerable: true,
    get: function() {
        return Gcs;
    }
});
const _fs = /*#__PURE__*/ _interop_require_default(require("fs"));
const _util = require("util");
const _stream = require("stream");
const _storage = require("@google-cloud/storage");
const _axios = /*#__PURE__*/ _interop_require_default(require("axios"));
const _requestfilteringagent = require("request-filtering-agent");
const _pluginUtils = require("~/utils/pluginUtils");
const _ncError = require("~/helpers/ncError");
function _extends() {
    _extends = Object.assign || function(target) {
        for(var i = 1; i < arguments.length; i++){
            var source = arguments[i];
            for(var key in source){
                if (Object.prototype.hasOwnProperty.call(source, key)) {
                    target[key] = source[key];
                }
            }
        }
        return target;
    };
    return _extends.apply(this, arguments);
}
function _interop_require_default(obj) {
    return obj && obj.__esModule ? obj : {
        default: obj
    };
}
let Gcs = class Gcs {
    patchKey(key) {
        let patchedKey = decodeURIComponent(key);
        if (patchedKey.startsWith(`${this.bucketName}/`)) {
            patchedKey = patchedKey.replace(`${this.bucketName}/`, '');
        }
        return patchedKey;
    }
    aclConfig() {
        return this.input.uniform_bucket_level_access ? {} : {
            predefinedAcl: 'publicRead'
        };
    }
    async init() {
        const options = {
            credentials: {
                client_email: this.input.client_email,
                // replace \n with real line breaks to avoid
                // error:0909006C:PEM routines:get_name:no start line
                private_key: this.input.private_key.replace(/\\n/gm, '\n')
            }
        };
        if (this.input.project_id) {
            options.projectId = this.input.project_id;
        }
        this.bucketName = this.input.bucket;
        this.storageClient = new _storage.Storage(options);
    }
    async test() {
        try {
            const tempFile = (0, _pluginUtils.generateTempFilePath)();
            const createStream = _fs.default.createWriteStream(tempFile);
            await (0, _pluginUtils.waitForStreamClose)(createStream);
            await this.fileCreate('nc-test-file.txt', {
                path: tempFile,
                mimetype: 'text/plain',
                originalname: 'temp.txt',
                size: createStream.bytesWritten.toString()
            });
            await (0, _util.promisify)(_fs.default.unlink)(tempFile);
            return true;
        } catch (e) {
            _ncError.NcError.pluginTestError(e == null ? void 0 : e.message);
        }
    }
    async fileRead(key) {
        try {
            const file = this.storageClient.bucket(this.bucketName).file(this.patchKey(key));
            const [exists] = await file.exists();
            if (!exists) {
                _ncError.NcError._.storageFileReadError(`File ${this.patchKey(key)} does not exist`);
            }
            const [data] = await file.download();
            return data;
        } catch (e) {
            _ncError.NcError._.storageFileReadError(e.message);
        }
    }
    async fileCreate(key, file) {
        try {
            const [uploadResponse] = await this.storageClient.bucket(this.bucketName).upload(file.path, _extends({
                destination: this.patchKey(key),
                contentType: (file == null ? void 0 : file.mimetype) || 'application/octet-stream',
                gzip: true,
                metadata: {
                    cacheControl: 'public, max-age=31536000'
                }
            }, this.aclConfig()));
            return uploadResponse.publicUrl();
        } catch (e) {
            _ncError.NcError._.storageFileCreateError(e.message);
        }
    }
    async fileCreateByStream(key, stream, options = {}) {
        try {
            const file = this.storageClient.bucket(this.bucketName).file(this.patchKey(key));
            await new Promise((resolve, reject)=>{
                stream.pipe(file.createWriteStream(_extends({
                    gzip: true,
                    metadata: {
                        contentType: options.mimetype || 'application/octet-stream',
                        cacheControl: 'public, max-age=31536000'
                    }
                }, this.aclConfig()))).on('finish', ()=>resolve()).on('error', reject);
            });
            return file.publicUrl();
        } catch (e) {
            _ncError.NcError._.storageFileStreamError(e.message);
        }
    }
    async fileCreateByUrl(destPath, url, { fetchOptions: { buffer } = {
        buffer: false
    } }) {
        try {
            const response = await _axios.default.get(url, {
                httpAgent: (0, _requestfilteringagent.useAgent)(url),
                httpsAgent: (0, _requestfilteringagent.useAgent)(url),
                responseType: buffer ? 'arraybuffer' : 'stream'
            });
            const file = this.storageClient.bucket(this.bucketName).file(destPath);
            await file.save(response.data);
            return {
                url: file.publicUrl(),
                data: response.data
            };
        } catch (e) {
            _ncError.NcError._.storageFileCreateError(`Failed to create file from URL: ${e.message}`);
        }
    }
    async fileDelete(path) {
        try {
            await this.storageClient.bucket(this.bucketName).file(path).delete();
        } catch (e) {
            _ncError.NcError._.storageFileDeleteError(e.message);
        }
    }
    async fileReadByStream(key) {
        const file = this.storageClient.bucket(this.bucketName).file(this.patchKey(key));
        // Check if file exists before creating stream
        const [exists] = await file.exists();
        if (!exists) {
            _ncError.NcError._.storageFileReadError(`File ${this.patchKey(key)} does not exist`);
        }
        return file.createReadStream();
    }
    async getDirectoryList(path) {
        try {
            const [files] = await this.storageClient.bucket(this.bucketName).getFiles({
                prefix: path
            });
            return files.map((file)=>file.name);
        } catch (e) {
            _ncError.NcError._.storageFileReadError(`Failed to list directory: ${e.message}`);
        }
    }
    async getSignedUrl(key, expiresInSeconds = 7200, pathParameters) {
        try {
            const options = {
                version: 'v4',
                action: 'read',
                expires: Date.now() + expiresInSeconds * 1000,
                responseDisposition: pathParameters == null ? void 0 : pathParameters.ResponseContentDisposition,
                responseType: pathParameters == null ? void 0 : pathParameters.ResponseContentType
            };
            const [url] = await this.storageClient.bucket(this.bucketName).file(this.patchKey(key)).getSignedUrl(options);
            return url;
        } catch (e) {
            _ncError.NcError._.storageFileReadError(`Failed to generate signed URL: ${e.message}`);
        }
    }
    async scanFiles(globPattern) {
        // Remove all dots from the prefix
        globPattern = globPattern.replace(/\./g, '');
        // Remove the leading slash
        globPattern = globPattern.replace(/^\//, '');
        // Make sure pattern starts with nc/uploads/
        if (!globPattern.startsWith('nc/uploads/')) {
            globPattern = `nc/uploads/${globPattern}`;
        }
        const stream = new _stream.Readable({
            objectMode: true,
            read () {}
        });
        try {
            const fileStream = this.storageClient.bucket(this.input.bucket).getFilesStream({
                prefix: globPattern,
                autoPaginate: true
            });
            fileStream.on('error', (error)=>{
                stream.destroy(error);
            });
            fileStream.on('data', (file)=>{
                stream.push(file.name);
            });
            fileStream.on('end', ()=>{
                stream.push(null);
            });
            return stream;
        } catch (e) {
            stream.destroy(new Error(`Failed to scan files: ${e.message}`));
            _ncError.NcError._.storageFileReadError(`Failed to scan files: ${e.message}`);
        }
    }
    getUploadedPath(path) {
        // TODO: verify this
        return {
            url: this.storageClient.bucket(this.bucketName).file(this.patchKey(path)).publicUrl()
        };
    }
    constructor(input){
        this.name = 'Gcs';
        this.input = input;
    }
};
